/**
 * Color picker popover (screenshots/78): theme swatches (chart colors + scale) and a custom color (saturation/value
 * area, hue, hex, opacity), with the contrast and color-vision checks for the color being picked. Hovering a swatch
 * previews it on the widget; leaving puts it back; a click applies it as one undo step (81 #6).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { CIcon } from "../icons";
import { resolveColor, type ColorRef } from "../chartSettings";
import type { Palette } from "../chartOptions";
import { contrast, isHex, simulate } from "../paletteChecks";
import { Seg } from "./ui";

export const TOKEN_NAME: Record<string, string> = { "chart-1": "Chart 1", "chart-2": "Chart 2", "chart-3": "Chart 3", muted: "Muted", "seq-1": "Scale 1", "seq-2": "Scale 2", "seq-3": "Scale 3", "seq-4": "Scale 4" };
export const refName = (r: ColorRef) => TOKEN_NAME[r] ?? r.toUpperCase();

const hsv2hex = (h: number, s: number, v: number) => {
  const f = (n: number) => { const k = (n + h / 60) % 6; return v - v * s * Math.max(0, Math.min(k, 4 - k, 1)); };
  return `#${[f(5), f(3), f(1)].map((x) => Math.round(x * 255).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
};
const hex2hsv = (hex: string): [number, number, number] => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const max = Math.max(r, g, b), d = max - Math.min(r, g, b);
  const h = d === 0 ? 0 : max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [((h * 60) + 360) % 360, max ? d / max : 0, max];
};

export interface ColorPickerProps {
  value: ColorRef; palette: Palette; title: string;
  /** What the picked color must stay apart from (e.g. Muted for the highlight). */
  against?: { ref: ColorRef; name: string };
  onPick: (r: ColorRef) => void;
  onHover?: (r: ColorRef | null) => void;
  onSaveToTheme?: () => void;
  /** The trigger's contents (a swatch, or a swatch + name like a select). */
  children?: ReactNode;
  triggerClass?: string;
}

export function ColorPicker({ value, palette, title, against, onPick, onHover, onSaveToTheme, children, triggerClass }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"theme" | "custom">("theme");
  const [pending, setPending] = useState<ColorRef | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  // The popover sits to the right of its field, outside the scrolling panel (position: fixed; 78).
  const openAt = () => {
    const r = (root.current?.closest(".cs-select, .cs-rule, .cs-row") ?? root.current)!.getBoundingClientRect();
    setPos({ left: Math.min(r.right + 16, innerWidth - 316), top: Math.max(8, Math.min(r.top - 26, innerHeight - 470)) });
    setOpen(true);
  };
  const root = useRef<HTMLSpanElement>(null);
  const cur = resolveColor(pending ?? value, palette).slice(0, 7).toUpperCase();
  const close = (revert = true) => { setOpen(false); setPending(null); if (revert) onHover?.(null); };
  const choose = (r: ColorRef) => { setPending(r); onHover?.(r); };
  useEffect(() => {
    if (!open) return;
    const out = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) close(); };
    addEventListener("pointerdown", out);
    return () => removeEventListener("pointerdown", out);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const swatch = (r: ColorRef) => (
    <button key={r} type="button" className="cs-cp__sw" aria-label={refName(r)} aria-pressed={r === (pending ?? value)} style={{ background: resolveColor(r, palette) }}
      onMouseEnter={() => onHover?.(r)} onMouseLeave={() => onHover?.(pending)} onFocus={() => onHover?.(r)}
      onClick={() => { onPick(r); close(false); }} />
  );
  const cr = contrast(cur, "#FFFFFF");
  const vs = against ? resolveColor(against.ref, palette) : null;
  const apart = vs ? Math.min(...(["protan", "deutan", "tritan", "gray"] as const).map((k) => contrast(simulate(cur, k), simulate(vs, k)))) : null;

  return (
    <span className="color-picker" ref={root}>
      <button type="button" className={triggerClass ?? "color-picker__swatch"} aria-haspopup="dialog" aria-expanded={open} aria-label={`${title}: ${refName(value)}`}
        style={triggerClass ? undefined : { background: resolveColor(value, palette) }} onClick={() => (open ? close() : openAt())}>{children}</button>
      {open && (
        <div className="cs-cp" role="dialog" aria-label={title} style={pos ?? undefined} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); } }}>
          <div className="cs-cp__head"><strong>{title}</strong>
            <Seg label="Color source" value={mode} onChange={setMode} options={[{ id: "theme", label: "Theme" }, { id: "custom", label: "Custom" }]} /></div>
          {mode === "theme" && (<>
            <span className="cs-cp__k">Chart colors</span><div className="cs-cp__row">{["chart-1", "chart-2", "chart-3", "muted"].map(swatch)}</div>
            <span className="cs-cp__k">Scale</span><div className="cs-cp__row">{["seq-1", "seq-2", "seq-3", "seq-4"].map(swatch)}</div>
          </>)}
          <Custom hex={cur} onChange={choose} />
          <ul className="cs-cp__checks">
            <li className={cr >= 3 ? "is-pass" : "is-fail"}><CIcon name={cr >= 3 ? "circle-check" : "circle-x"} size={12} />{cr.toFixed(1)}:1 on white · {cr >= 3 ? "OK for bars" : "marks need 3:1"}</li>
            {against && apart !== null && <li className={apart >= 2 ? "is-pass" : "is-fail"}><CIcon name={apart >= 2 ? "circle-check" : "circle-x"} size={12} />
              {apart >= 2 ? `Distinct from ${against.name} in all simulations` : `Too close to ${against.name}: ${apart.toFixed(1)}:1 apart`}</li>}
          </ul>
          <div className="cs-cp__foot">
            {onSaveToTheme && <button type="button" className="link-button" onClick={onSaveToTheme}>Save to theme</button>}
            <span className="spacer" />
            <button type="button" className="btn btn--secondary btn--small" onClick={() => close()}>Cancel</button>
            <button type="button" className="btn btn--primary btn--small" onClick={() => { onPick(pending ?? value); close(false); }}>Apply</button>
          </div>
        </div>
      )}
    </span>
  );
}

/** Saturation/value area + hue slider + hex + opacity. Arrow keys move the handles (1%, Shift = 10%). */
function Custom({ hex, onChange }: { hex: string; onChange: (r: ColorRef) => void }) {
  const [hsv, setHsv] = useState(() => hex2hsv(hex));
  const [text, setText] = useState(hex.slice(1));
  const [alpha, setAlpha] = useState(100);
  useEffect(() => { if (hsv2hex(...hsv) !== hex) { setHsv(hex2hsv(hex)); setText(hex.slice(1)); } }, [hex]); // eslint-disable-line react-hooks/exhaustive-deps
  const emit = (h: [number, number, number], a = alpha) => {
    setHsv(h); const x = hsv2hex(...h); setText(x.slice(1));
    onChange(a < 100 ? `${x}${Math.round((a / 100) * 255).toString(16).padStart(2, "0").toUpperCase()}` : x);
  };
  const drag = (el: HTMLElement, e: React.PointerEvent, fn: (x: number, y: number) => void) => {
    el.setPointerCapture(e.pointerId);
    const at = (ev: { clientX: number; clientY: number }) => { const r = el.getBoundingClientRect(); fn(Math.min(1, Math.max(0, (ev.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (ev.clientY - r.top) / r.height))); };
    at(e);
    const move = (ev: PointerEvent) => at(ev);
    const up = () => { el.removeEventListener("pointermove", move); el.removeEventListener("pointerup", up); };
    el.addEventListener("pointermove", move); el.addEventListener("pointerup", up);
  };
  const [h, s, v] = hsv;
  return (
    <div className="cs-cp__custom">
      <div className="cs-cp__sv" style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hsv2hex(h, 1, 1)})` }}
        role="slider" tabIndex={0} aria-label="Saturation and brightness" aria-valuetext={`Saturation ${Math.round(s * 100)}%, brightness ${Math.round(v * 100)}%`}
        onPointerDown={(e) => drag(e.currentTarget, e, (x, y) => emit([h, x, 1 - y]))}
        onKeyDown={(e) => { const d = e.shiftKey ? 0.1 : 0.01; const m: Record<string, [number, number]> = { ArrowRight: [d, 0], ArrowLeft: [-d, 0], ArrowUp: [0, d], ArrowDown: [0, -d] };
          const k = m[e.key]; if (k) { e.preventDefault(); emit([h, Math.min(1, Math.max(0, s + k[0])), Math.min(1, Math.max(0, v + k[1]))]); } }}>
        <i style={{ left: `${s * 100}%`, top: `${(1 - v) * 100}%` }} />
      </div>
      <div className="cs-cp__hue" role="slider" tabIndex={0} aria-label="Hue" aria-valuemin={0} aria-valuemax={360} aria-valuenow={Math.round(h)}
        onPointerDown={(e) => drag(e.currentTarget, e, (x) => emit([x * 359, s, v]))}
        onKeyDown={(e) => { const d = (e.shiftKey ? 10 : 1) * (e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : 0);
          if (d) { e.preventDefault(); emit([(h + d + 360) % 360, s, v]); } }}>
        <i style={{ left: `${(h / 360) * 100}%` }} />
      </div>
      <div className="cs-cp__hex">
        <span className="cs-cp__now" style={{ background: hex }} />
        <label className="cs-cp__field"><span aria-hidden>#</span><input aria-label="Hex color" value={text} maxLength={6}
          onChange={(e) => { const t = e.target.value.replace(/[^0-9a-f]/gi, "").toUpperCase(); setText(t); if (isHex(t)) { const x = `#${t}`; setHsv(hex2hsv(x)); onChange(x); } }} /></label>
        <label className="cs-cp__field cs-cp__field--a"><input aria-label="Opacity" value={`${alpha}%`}
          onChange={(e) => { const a = Math.min(100, Math.max(0, Number(e.target.value.replace(/\D/g, "")) || 0)); setAlpha(a); emit(hsv, a); }} /></label>
      </div>
    </div>
  );
}
